import type { BrowserOptions, ErrorEvent, User } from '@sentry/nextjs'

/** `@sentry/nextjs` doesn't re-export the type; take it from the option. */
export type TransactionEvent = Parameters<NonNullable<BrowserOptions['beforeSendTransaction']>>[0]

/*
 * Browser Sentry: the options `instrumentation-client.ts` passes to
 * `Sentry.init`, and the filters that run on every event before it leaves
 * the browser. Kept free of SDK calls so the rules are unit-testable.
 *
 * Why it lives here and not in `sentry.client.config.ts`: Next 16 builds with
 * Turbopack, and `@sentry/nextjs` only injects `sentry.client.config.ts` in
 * webpack builds. The file was never loaded, so the browser SDK shipped but
 * never started (ALU-79). The init now runs from `instrumentation-client.ts`,
 * which Next loads in every browser bundle.
 *
 * What is sent: uncaught errors and unhandled promise rejections (the SDK's
 * default handlers) plus a small sample of page-load and navigation traces.
 * No console capture, no user feedback widget. Session replay is loaded only
 * after analytics consent (`lib/observability/sentry-replay.ts`).
 *
 * Personal data: `sendDefaultPii` stays off (no IP inference, no cookies),
 * `user` is cut down to its id, and email addresses and international phone
 * numbers are masked in every string of the event. The request headers stay
 * (the SDK only adds User-Agent and Referer): Sentry reads the browser and OS
 * from the User-Agent.
 */

export interface SentryClientEnv {
  dsn: string | undefined
  /** `NEXT_PUBLIC_APP_ENV` (production | staging | development). */
  appEnv: string | undefined
  nodeEnv: string | undefined
  /**
   * Tags added to every event when it is sent, not at init: the platform
   * check (lib/utils/platform.ts) caches its first answer, and at init the
   * Capacitor bridge may not exist yet.
   */
  sendTimeTags?: () => Record<string, string>
}

/** Share of page loads and navigations traced in a production build. */
export const PRODUCTION_TRACES_SAMPLE_RATE = 0.1

/** Replay sampling, applied only once replay is loaded (after consent). */
export const REPLAY_SESSION_SAMPLE_RATE = 0.01
export const REPLAY_ON_ERROR_SAMPLE_RATE = 1.0

/**
 * The options for `Sentry.init`. No `integrations`: the defaults of
 * `@sentry/nextjs` (error and unhandled-rejection handlers, breadcrumbs,
 * page-load and navigation tracing) are what we want, and replay is added
 * after consent. Disabled when the build has no DSN, so local builds without
 * one send nothing.
 */
export function sentryClientOptions(env: SentryClientEnv): BrowserOptions {
  const dsn = env.dsn?.trim() || undefined
  const tag = <E extends ErrorEvent | TransactionEvent>(event: E): E => {
    if (!env.sendTimeTags) return event
    return { ...event, tags: { ...event.tags, ...env.sendTimeTags() } }
  }
  return {
    dsn,
    enabled: !!dsn,
    environment: env.appEnv || 'development',
    sendDefaultPii: false,
    tracesSampleRate: env.nodeEnv === 'production' ? PRODUCTION_TRACES_SAMPLE_RATE : 1.0,
    // Read by the replay integration if and when it is added.
    replaysSessionSampleRate: REPLAY_SESSION_SAMPLE_RATE,
    replaysOnErrorSampleRate: REPLAY_ON_ERROR_SAMPLE_RATE,
    // First-pass filter: stacks that start in a browser extension.
    denyUrls: [/^chrome-extension:\/\//, /^moz-extension:\/\//, /^safari-web-extension:\/\//],
    beforeSend: (event) => {
      const kept = filterClientSentryEvent(event)
      return kept ? tag(scrubClientEvent(kept)) : null
    },
    beforeSendTransaction: (event) => tag(scrubClientEvent(event)),
  }
}

const EXTENSION_PREFIXES = ['chrome-extension://', 'moz-extension://', 'safari-web-extension://']

/**
 * Noise rules for browser error events. Returning `null` drops the event.
 * Mirrors the PostHog `before_send` filter in `instrumentation-client.ts` and
 * the server filter in `sentry-server-filters.ts`.
 */
export function filterClientSentryEvent(event: ErrorEvent): ErrorEvent | null {
  const values = event.exception?.values ?? []
  const message = values[0]?.value || event.message || ''

  // Any frame of any exception in the chain from a browser extension.
  const fromExtension = values.some((value) =>
    (value.stacktrace?.frames ?? []).some((frame) =>
      EXTENSION_PREFIXES.some((prefix) => (frame.filename ?? '').startsWith(prefix))
    )
  )
  if (fromExtension) return null

  if (
    // Network errors expected on mobile (offline, content blockers).
    message.includes('Failed to fetch') ||
    message.includes('Load failed') ||
    message.includes('NetworkError') ||
    // Browser quirk, not a bug.
    message.includes('ResizeObserver loop') ||
    // Cross-origin script errors carry no stack or context.
    message === 'Script error.' ||
    message === 'Script error' ||
    // Password-manager and translation extensions.
    message.includes('Object Not Found Matching Id') ||
    // Deploy skew: a tab from build N calls a chunk or Server Action that
    // build N+1 retired. app/error.tsx and instrumentation-client.ts reload
    // once, so these heal themselves. Sentry: JAVASCRIPT-NEXTJS-2D, -2S.
    message.includes('ChunkLoadError') ||
    message.includes('module factory is not available') ||
    message.includes('Loading chunk') ||
    message.includes('Failed to fetch dynamically imported module') ||
    message.includes('UnrecognizedActionError') ||
    /server action.*was not found on the server/i.test(message) ||
    /failed to find server action/i.test(message) ||
    /router state header.*could not be parsed/i.test(message)
  ) {
    return null
  }

  return event
}

const EMAIL = /[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi
// Encoded `@` as it appears in URLs and query strings.
const EMAIL_URL_ENCODED = /[A-Z0-9._%+-]+%40[A-Z0-9.-]+\.[A-Z]{2,}/gi
// International format only (`+` then 8-15 digits, with the usual
// separators), so ids, timestamps and line numbers are left alone.
const PHONE = /\+\d(?:[\s().-]?\d){7,14}/g

export const REDACTED_EMAIL = '[email]'
export const REDACTED_PHONE = '[phone]'

export function scrubString(value: string): string {
  return value
    .replace(EMAIL, REDACTED_EMAIL)
    .replace(EMAIL_URL_ENCODED, REDACTED_EMAIL)
    .replace(PHONE, REDACTED_PHONE)
}

function scrubDeep(value: unknown, depth: number): unknown {
  if (typeof value === 'string') return scrubString(value)
  if (depth > 12 || value === null || typeof value !== 'object') return value
  if (Array.isArray(value)) return value.map((item) => scrubDeep(item, depth + 1))
  const out: Record<string, unknown> = {}
  for (const [key, item] of Object.entries(value)) out[key] = scrubDeep(item, depth + 1)
  return out
}

/** Only the id survives; email, username, IP and anything else are dropped. */
function userIdOnly(user: User | undefined): User | undefined {
  if (!user) return undefined
  return user.id !== undefined ? { id: user.id } : undefined
}

/**
 * Removes personal data from an event before it is sent: `user` down to its
 * id, no cookies, and emails and phone numbers masked in
 * every string (messages, exception values, breadcrumbs, URLs, span
 * descriptions, contexts, extra data).
 */
export function scrubClientEvent<E extends ErrorEvent | TransactionEvent>(event: E): E {
  const scrubbed = scrubDeep(event, 0) as E
  const user = userIdOnly(event.user)
  if (user) scrubbed.user = user
  else delete scrubbed.user
  if (scrubbed.request) delete scrubbed.request.cookies
  return scrubbed
}
